import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/styles/notifications.css';
import { BACKEND_URL } from '@/config';
import {
  Banknote, Bell, BellOff, CircleCheck, ClipboardCheck, CreditCard, LoaderCircle, MailPlus, MessageCircle, Package,
  ShieldCheck, ShieldX, Star, TriangleAlert, Truck, UserCheck, UserMinus, UserX, X,
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const TYPE_ICONS = {
  proposition_received:  MailPlus,
  proposition_accepted:  CircleCheck,
  proposition_rejected:  X,
  mission_accepted:      ClipboardCheck,
  mission_paid:          CreditCard,
  mission_in_transit:    Truck,
  mission_delivered:     Package,
  mission_completed:     Banknote,
  mission_disputed:      TriangleAlert,
  message_new:           MessageCircle,
  rating_received:       Star,
  kyc_approved:          ShieldCheck,
  kyc_rejected:          ShieldX,
  role_approved:         UserCheck,
  role_rejected:         UserX,
  role_renounced:        UserMinus,
};

const TYPE_TONES = {
  proposition_received: 'gold',
  proposition_accepted: 'teal',
  proposition_rejected: 'red',
  mission_accepted:     'teal',
  mission_in_transit:   'navy',
  mission_delivered:    'gold',
  mission_completed:    'teal',
  mission_disputed:     'red',
  message_new:          'navy',
  rating_received:      'gold',
  kyc_approved:         'teal',
  kyc_rejected:         'red',
  role_approved:        'teal',
  role_rejected:        'red',
  role_renounced:       'navy',
};

function fmtAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `il y a ${d} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function NotificationsDropdown({ user }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const fetchBadge = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/badge`);
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications?limit=20`);
      setItems(r.data?.data || []);
      setUnread(r.data?.meta?.unread || 0);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  // Initial + polling
  useEffect(() => {
    if (!user) return;
    fetchBadge();
    const iv = setInterval(fetchBadge, 45000);
    return () => clearInterval(iv);
  }, [user, fetchBadge]);

  // Fetch full list when opened
  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClickItem = async (n) => {
    if (!n.read) {
      try { await axios.patch(`${API}/notifications/${n.id}/read`); } catch { /* */ }
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) {
      // Use react-router for internal links
      if (n.link.startsWith('/')) navigate(n.link);
      else window.location.href = n.link;
    }
  };

  const handleMarkAll = async (e) => {
    e.stopPropagation();
    if (unread === 0) return;
    try {
      await axios.patch(`${API}/notifications/read-all`);
      setItems(prev => prev.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* */ }
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="topbar-notif"
        aria-label={`Notifications${unread ? ` (${unread} non lues)` : ''}`}
        data-testid="btn-notifications"
        onClick={() => setOpen(o => !o)}
      >
        <Bell size={18} aria-hidden="true" />
        {unread > 0 && (
          <span className="notif-badge" data-testid="notif-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications" data-testid="notif-panel">
          <div className="notif-panel-head">
            <div>
              <div className="notif-panel-title">Notifications</div>
              <div className="notif-panel-sub">{unread} non lue{unread > 1 ? 's' : ''}</div>
            </div>
            <button
              type="button"
              className="notif-mark-all"
              onClick={handleMarkAll}
              disabled={unread === 0}
              data-testid="btn-mark-all-read"
            >
              Tout marquer comme lu
            </button>
          </div>

          <div className="notif-panel-body">
            {loading ? (
              <div className="notif-empty"><LoaderCircle size={14} className="notif-spin" aria-hidden="true" /> Chargement…</div>
            ) : items.length === 0 ? (
              <div className="notif-empty">
                <BellOff size={36} className="notif-empty-ico" aria-hidden="true" />
                <div>Aucune notification pour le moment</div>
              </div>
            ) : (
              items.map(n => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                const tone = TYPE_TONES[n.type] || 'navy';
                return (
                  <button
                    key={n.id}
                    type="button"
                    className={`notif-row${n.read ? '' : ' is-unread'}`}
                    onClick={() => handleClickItem(n)}
                    data-testid={`notif-row-${n.id}`}
                  >
                    <div className={`notif-icon tone-${tone}`}>
                      <Icon size={16} aria-hidden="true" />
                    </div>
                    <div className="notif-text">
                      <div className="notif-row-title">{n.title}</div>
                      {n.body && <div className="notif-row-body">{n.body}</div>}
                      <div className="notif-row-time">{fmtAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="notif-row-dot" aria-hidden="true" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
